<template>
    <el-container class="list-page">
        <el-header class="list-page-header">
            <h1 class="list-page-title">{{userSetting.invoiceLabel}}</h1>
            <div class="list-page-search">
                <el-input prefix-icon="el-icon-search" v-model="searchContent"
                          placeholder="Search your invoices"></el-input>
            </div>
            <el-button class="list-page-add-button" type="primary" :disabled="cantAddInvoice" @click.native.prevent="goAddInvoice">
                <i class="el-icon-plus"></i>
                <span>Add invoice</span>
            </el-button>
        </el-header>
        <el-main class="list-page-main">
            <template>
                <div class="list-summary-grid">
                    <div class="list-summary-card overdue">
                        <div class="list-summary-icon">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M14 2H6.5A2.5 2.5 0 0 0 4 4.5v15A2.5 2.5 0 0 0 6.5 22h11a2.5 2.5 0 0 0 2.5-2.5V8l-6-6Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                                <path d="M14 2v6h6" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                                <path d="M12 11v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                <path d="M12 18h.01" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                            </svg>
                        </div>
                        <div class="list-summary-content">
                            <div class="list-summary-title">Overdue invoices ({{invoiceSummary.overdueInvoiceCount}})</div>
                            <div class="list-summary-money">{{formatSummaryMoney(invoiceSummary.overdueInvoiceTotalMoney)}}</div>
                        </div>
                    </div>
                    <div class="list-summary-card unpaid">
                        <div class="list-summary-icon">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                                <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <div class="list-summary-content">
                            <div class="list-summary-title">Unpaid invoices ({{invoiceSummary.unpaidInvoiceCount}})</div>
                            <div class="list-summary-money">{{formatSummaryMoney(invoiceSummary.unpaidInvoiceTotalMoney)}}</div>
                        </div>
                    </div>
                    <div class="list-summary-card unsent">
                        <div class="list-summary-icon">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="m22 2-7 20-4-9-9-4 20-7Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                                <path d="M22 2 11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </div>
                        <div class="list-summary-content">
                            <div class="list-summary-title">Unsent invoices ({{invoiceSummary.unsentInvoiceCount}})</div>
                            <div class="list-summary-money">{{formatSummaryMoney(invoiceSummary.unsentInvoiceTotalMoney)}}</div>
                        </div>
                    </div>
                </div>
                <div class="list-table-card">
                    <el-table class="list-table" @sort-change="sortTable" v-loading="loading" :data="tableData"
                              :default-sort="{prop: 'invoiceStartDate', order: 'descending'}"
                              @row-click='handleRowHandle'>
                    <el-table-column prop="numPrefix,num" label="Number" sortable="custom" min-width="130">
                        <template slot="header">
                            <span class="list-sort-header" :class="{'is-active': orderName === 'numPrefix,num'}">
                                Number
                                <svg v-if="orderName === 'numPrefix,num'" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path v-if="orderName === 'numPrefix,num' && orderTpe === 1" d="M8 6L12 2L16 6"/>
                                    <path v-else d="M8 18L12 22L16 18"/>
                                    <path d="M12 2V22"/>
                                </svg>
                            </span>
                        </template>
                        <template slot-scope="scope"> {{scope.row.numPrefix}}{{scope.row.num}}</template>
                    </el-table-column>
                    <el-table-column prop="userClientName" label="Client" min-width="150">
                        <template slot-scope="scope">
                            <span class="list-client-name" v-if="scope.row.userClientName">{{scope.row.userClientName}}</span>
                            <span class="list-cell-empty" v-else>—</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="invoiceStartDate" :label="userSetting.dateLabel" :formatter="dateFormat" sortable="custom" min-width="150">
                        <template slot="header">
                            <span class="list-sort-header" :class="{'is-active': orderName === 'invoiceStartDate'}">
                                {{userSetting.dateLabel}}
                                <svg v-if="orderName === 'invoiceStartDate'" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path v-if="orderName === 'invoiceStartDate' && orderTpe === 1" d="M8 6L12 2L16 6"/>
                                    <path v-else d="M8 18L12 22L16 18"/>
                                    <path d="M12 2V22"/>
                                </svg>
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="dueDate" :label="userSetting.dueDateLabel" sortable="custom" min-width="180">
                        <template slot="header">
                            <span class="list-sort-header" :class="{'is-active': orderName === 'dueDate'}">
                                {{userSetting.dueDateLabel}}
                                <svg v-if="orderName === 'dueDate'" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path v-if="orderName === 'dueDate' && orderTpe === 1" d="M8 6L12 2L16 6"/>
                                    <path v-else d="M8 18L12 22L16 18"/>
                                    <path d="M12 2V22"/>
                                </svg>
                            </span>
                        </template>
                        <template slot-scope="scope">
                            <span v-if="formatDueDate2(scope.row.dueDate,scope)">{{formatDueDate2(scope.row.dueDate,scope)}}</span>
                            <span class="list-cell-empty" v-else>—</span>
                            <span class="list-status-badge list-status-green" v-if="scope.row.paidStatus=== 2 && scope.row.totalMoney>0">Fully paid</span>
                            <span class="list-status-badge list-status-orange" v-else-if="scope.row.paidStatus=== 1 && Date.parse(new Date())<=scope.row.dueDate*1000">Partial</span>
                            <span class="list-status-badge list-status-red" v-else-if="Date.parse(new Date())>scope.row.dueDate*1000 && scope.row.totalMoney>0">Overdue</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="emailed" label="Status" min-width="130">
                        <template slot-scope="scope">
                            <span class="list-status-badge list-status-blue" v-if="scope.row.emailed=== 0">UnSent</span>
                            <span class="list-status-badge list-status-green" v-else-if="scope.row.emailed=== 1">Sent</span>
                            <span class="list-status-badge list-status-purple" v-else-if="scope.row.emailed=== 2">Opened</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="totalMoney" :formatter="amountFormat" :label="userSetting.totalLabel" sortable="custom" min-width="130" align="right" header-align="right">
                        <template slot="header">
                            <span class="list-sort-header" :class="{'is-active': orderName === 'totalMoney'}">
                                {{userSetting.totalLabel}}
                                <svg v-if="orderName === 'totalMoney'" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path v-if="orderName === 'totalMoney' && orderTpe === 1" d="M8 6L12 2L16 6"/>
                                    <path v-else d="M8 18L12 22L16 18"/>
                                    <path d="M12 2V22"/>
                                </svg>
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="balanceDueMoney" :formatter="amountFormat" :label="userSetting.balanceDueLabel" min-width="150" align="right" header-align="right">
                    </el-table-column>
                    <el-table-column min-width="70" align="right" header-align="right" class-name="list-action-column">
                        <template slot-scope="scope">
                            <el-dropdown trigger="click" @command="handleInvoiceAction($event, scope.row)" @click.native.stop>
                                <button type="button" class="invoice-list-action-button" @click.stop>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2F77F1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <circle cx="12" cy="12" r="1"/>
                                        <circle cx="12" cy="5" r="1"/>
                                        <circle cx="12" cy="19" r="1"/>
                                    </svg>
                                </button>
                                <el-dropdown-menu slot="dropdown" class="invoice-list-action-menu">
                                    <el-dropdown-item command="recordPayment">Record Payment</el-dropdown-item>
                                    <el-dropdown-item command="markAsSent">Mark as Sent</el-dropdown-item>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </template>
                    </el-table-column>
                </el-table>
                </div>
                <div class="list-pagination">
                    <el-pagination
                            @size-change="changeTotalCount"
                            @current-change="changePage"
                            :current-page="currentPage"
                            :page-sizes="[50, 100, 200, 400]"
                            :page-size="pageSize"
                            layout="total, sizes, prev, pager, next, jumper"
                            :total="totalCount">
                    </el-pagination>
                </div>

            </template>
        </el-main>
        <el-dialog title="Record Payment" :visible.sync="recordPaymentDialog" width="520px" custom-class="record-payment-dialog">
            <el-form class="record-payment-form" :model="recordPaymentForm" label-position="top">
                <div class="record-payment-switch-row">
                    <span>Mark as fully paid</span>
                    <el-switch v-model="recordPaymentForm.isFullPay" :active-value=1 :inactive-value=0
                               @change="handleRecordPaymentFullPayChange"></el-switch>
                </div>
                <el-form-item label="Amount">
                    <el-input v-model="recordPaymentForm.paidMoney" @input="handleRecordPaymentAmountInput"></el-input>
                </el-form-item>
                <el-form-item label="Date">
                    <el-date-picker class="record-payment-date-picker" type="date"
                                    v-model="recordPaymentForm.paidDate" :clearable=false></el-date-picker>
                </el-form-item>
                <el-form-item label="Payment Method">
                    <el-input v-model="recordPaymentForm.paidType"></el-input>
                </el-form-item>
                <el-form-item label="Note">
                    <el-input type="textarea" :rows="3" v-model="recordPaymentForm.note"></el-input>
                </el-form-item>
            </el-form>
            <div slot="footer" class="dialog-footer record-payment-footer">
                <el-button @click="recordPaymentDialog = false">Cancel</el-button>
                <el-button type="primary" :loading="recordPaymentLoading" @click="saveRecordPayment">Save</el-button>
            </div>
        </el-dialog>
    </el-container>
</template>

<script>
    import {formatEnglishDate, getDaysByDateString} from '@/utils/time'
    import {timest, toTmpStartOfDayTime, uuid} from '@/utils/validate'
    import {debounce} from '@/utils/debounce'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {getCurrentCompanyUUID, syncCompanySelection} from '@/utils/company'
    import {openFastSpringPage} from '@/utils/subscription'

    export default {
        methods: {
            goAddInvoice() {
                this.loginInformation = JSON.parse(localStorage.getItem("loginInformation")).objectData;
                if (this.totalTypeCount > 5 && this.loginInformation.expiresTime * 1000 < Date.parse(new Date())) {
                    openFastSpringPage();
                    return;
                }

                let self = this;
                self.$router.push('/invoiceCreate');
            },
            queryCompanyAndInvoiceList() {
                let that = this;
                this.loading = true;
                this.cantAddInvoice = true;
                this.$axios.post('query_setting_and_company', {
                    params: {}
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        syncCompanySelection(response.data);
                        that.userSetting = JSON.parse(localStorage.getItem('userSetting'));
                        that.queryInvoiceDashboardSummary();
                        that.queryList();
                        return;
                    }
                    that.loading = false;
                    that.cantAddInvoice = false;
                }).catch(function () {
                    that.loading = false;
                    that.cantAddInvoice = false;
                })
            },
            queryList() {
                let that = this;
                this.loading = true;
                this.cantAddInvoice = true;
                this.$axios.post('query_invoice_list_user_web', {
                    params: {
                        "currentPage": that.currentPage,
                        "pageSize": that.pageSize,
                        "numberOrClient": that.searchContent,
                        "orderName": that.orderName,
                        "orderTpe": that.orderTpe,
                        "type": 0
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        that.tableData = response.data.dataList;
                        that.totalCount = response.data.totalCount;
                        that.totalTypeCount = response.data.totalTypeCount;
                        that.userSetting = JSON.parse(localStorage.getItem('userSetting'));
                    }
                    that.loading = false;
                    that.cantAddInvoice = false;

                }).catch(function () {
                    that.loading = false;
                    that.cantAddInvoice = false;
                })
            },
            queryInvoiceDashboardSummary() {
                this.$axios.post('query_invoice_dashboard_summary', {
                    params: {
                        type: 0,
                        companyUUID: getCurrentCompanyUUID(),
                        nowTime: timest()
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.invoiceSummary = response.data.objectData;
                    }
                }).catch(() => false)
            },
            sortTable(column) {
                if (!column.order) {
                    this.orderName = "";
                    this.orderTpe = "";
                    this.queryList();
                    return;
                }
                this.orderName = column.prop;
                if (column.order === "ascending") {
                    this.orderTpe = 1
                } else if (column.order === "descending") {
                    this.orderTpe = 2
                }
                this.queryList();
            },
            changePage(val) {
                this.currentPage = val;
                this.queryList();
            },
            changeTotalCount(val) {
                this.pageSize = val;
                this.queryList();
            },
            handleRowHandle(row, event, column) {
                //点击每一条列表数据触发的方法
                if (column.className === "el-table-column--selection" || column.className === "list-action-column") {
                    return false;
                } else {
                    //因为需要考虑详情里面的刷新 所以把拿过去的信息放在缓存里面
                    this.$router.push({path: 'invoiceUpdate', query: {innerUUID: row.innerUUID}});
                }
            },
            handleInvoiceAction(command, row) {
                if (command === 'markAsSent') {
                    this.markInvoiceAsSent(row);
                    return;
                }
                if (command === 'recordPayment') {
                    this.openRecordPaymentDialog(row);
                }
            },
            markInvoiceAsSent(row) {
                this.$axios.post('update_invoice', {
                    params: {
                        innerUUID: row.innerUUID,
                        emailed: 1
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        row.emailed = 1;
                        this.queryInvoiceDashboardSummary();
                    }
                }).catch(() => false)
            },
            openRecordPaymentDialog(row) {
                if (parseFloat(row.balanceDueMoney) <= 0) {
                    this.$message.error('More payment than balance due');
                    return;
                }
                this.recordPaymentInvoice = row;
                this.recordPaymentForm = {
                    paidMoney: row.balanceDueMoney,
                    paidDate: new Date(),
                    isFullPay: 1,
                    paidType: "",
                    note: ""
                };
                this.recordPaymentDialog = true;
            },
            handleRecordPaymentFullPayChange(value) {
                if (value === 1 && this.recordPaymentInvoice != null) {
                    this.recordPaymentForm.paidMoney = this.recordPaymentInvoice.balanceDueMoney;
                }
            },
            handleRecordPaymentAmountInput(value) {
                if (this.recordPaymentInvoice == null) {
                    return;
                }
                let inputMoney = parseFloat(value);
                let fullPayMoney = parseFloat(this.recordPaymentInvoice.balanceDueMoney);
                if (isNaN(inputMoney) || isNaN(fullPayMoney)) {
                    this.recordPaymentForm.isFullPay = 0;
                    return;
                }
                this.recordPaymentForm.isFullPay = inputMoney === fullPayMoney ? 1 : 0;
            },
            saveRecordPayment() {
                if (this.recordPaymentInvoice == null) {
                    return;
                }
                let paidMoney = parseFloat(this.recordPaymentForm.paidMoney);
                if (paidMoney <= 0 || isNaN(paidMoney)) {
                    this.$message.error('The amount must be greater than zero');
                    return;
                }

                if (this.recordPaymentForm.isFullPay === 1) {
                    paidMoney = parseFloat(this.recordPaymentInvoice.balanceDueMoney);
                }

                let payment = {
                    innerUUID: uuid(null, 37),
                    paidMoney: paidMoney,
                    paidType: this.recordPaymentForm.paidType,
                    createDate: timest(),
                    paidDate: toTmpStartOfDayTime(this.recordPaymentForm.paidDate),
                    syncStatus: 0,
                    accessDate: timest(),
                    updateTime: timest(),
                    note: this.recordPaymentForm.note,
                    fromClient: 3
                };
                this.recordPaymentLoading = true;
                this.$axios.post('web_query_invoice', {
                    params: {"innerUUID": this.recordPaymentInvoice.innerUUID}
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.saveInvoicePaymentFromDetail(response.data, payment);
                        return;
                    }
                    this.recordPaymentLoading = false;
                }).catch(() => {
                    this.recordPaymentLoading = false;
                })
            },
            getActiveUUIDArrayKey(list) {
                let uuidArrayKey = "";
                if (list == null) {
                    return uuidArrayKey;
                }
                for (let i = 0; i < list.length; i++) {
                    if (list[i].syncStatus !== 1) {
                        uuidArrayKey += list[i].innerUUID + ",";
                    }
                }
                return uuidArrayKey.endsWith(",") ? uuidArrayKey.substr(0, uuidArrayKey.length - 1) : uuidArrayKey;
            },
            buildInvoiceItemForSave(item, index) {
                let taxRateInfo = item.taxRateInfo == null ? "" : item.taxRateInfo;
                return {
                    itemDescription: item.itemDescription,
                    rate: item.rate,
                    quantity: item.quantity,
                    discount: item.discount,
                    discountAsPercent: item.discountAsPercent,
                    sortTag: index,
                    syncStatus: item.syncStatus,
                    withholdingAble: item.withholdingAble,
                    belongToInvoice: item.belongToInvoice,
                    allExclusiveTaxMoney: item.allExclusiveTaxMoney,
                    allInclusiveTaxMoney: item.allInclusiveTaxMoney,
                    subtotalMoney: item.subtotalMoney,
                    taxRateInfo: taxRateInfo.endsWith("&&") ? taxRateInfo.substr(0, taxRateInfo.length - 2) : taxRateInfo,
                    totalMoney: item.totalMoney,
                    withholdingMoney: item.withholdingMoney,
                    allTaxMoney: item.allTaxMoney,
                    taxType: item.taxType,
                    fromClient: 3,
                    innerUUID: item.innerUUID,
                    itemCode: item.itemCode,
                    cost: 0,
                    type: 0,
                    parentUUID: "",
                    createDate: timest(),
                    accessDate: timest()
                };
            },
            buildInvoiceClientForSave(userClient) {
                if (userClient == null) {
                    return {};
                }
                return {
                    name: userClient.name,
                    clientEmail: userClient.clientEmail,
                    phone: userClient.phone,
                    billingAddressLine1: userClient.billingAddressLine1,
                    billingAddressLine2: userClient.billingAddressLine2,
                    billingAddressLine3: userClient.billingAddressLine3,
                    shippingAddressLine1: userClient.shippingAddressLine1,
                    shippingAddressLine2: userClient.shippingAddressLine2,
                    shippingAddressLine3: userClient.shippingAddressLine3,
                    shippingName: userClient.shippingName,
                    note: userClient.note,
                    updateOrCreate: 1,
                    innerUUID: userClient.innerUUID,
                    createDate: timest(),
                    accessDate: timest(),
                    fromClient: 3,
                    isDelete: 0,
                    syncStatus: 0,
                    cell: "",
                    contact: "",
                    fax: "",
                    pager: "",
                    website: "",
                };
            },
            buildInvoiceForPaymentSave(invoice, itemList, payHistoryList, invoiceImageList, taxInfoList, payment) {
                let paidTotal = this.$NP.plus(invoice.paidMoney, payment.paidMoney);
                let withholdingMoney = invoice.withholdingMoney == null ? 0 : invoice.withholdingMoney;
                let paidStatus = 0;
                if (parseFloat(paidTotal.toFixed(4)) >= parseFloat(this.$NP.minus(invoice.totalMoney, withholdingMoney).toFixed(4))) {
                    paidStatus = 2;
                } else if (parseFloat(paidTotal.toFixed(4)) > 0 && parseFloat(paidTotal.toFixed(4)) < parseFloat(invoice.totalMoney.toFixed(4))) {
                    paidStatus = 1;
                }
                return {
                    innerUUID: invoice.innerUUID,
                    comment: invoice.comment,
                    createDate: timest(),
                    discount: invoice.discount,
                    discountAsPercent: invoice.discountAsPercent,
                    dueDate: invoice.dueDate,
                    emailed: invoice.emailed,
                    invoiceStartDate: invoice.invoiceStartDate,
                    num: invoice.num,
                    shipping: invoice.shipping,
                    shippingDate: invoice.shippingDate,
                    shippingFOB: invoice.shippingFOB,
                    shippingTracking: invoice.shippingTracking,
                    shippingVia: invoice.shippingVia,
                    status: "Issued",
                    syncStatus: 0,
                    terms: invoice.terms,
                    type: 0,
                    accessDate: timest(),
                    clientUUIDKey: invoice.clientUUIDKey,
                    itemUUIDArrayKey: this.getActiveUUIDArrayKey(itemList),
                    payhistoryUUIDArrayKey: this.getActiveUUIDArrayKey(payHistoryList),
                    invoiceImageUUIDArrayKey: this.getActiveUUIDArrayKey(invoiceImageList),
                    updateTime: timest(),
                    allExclusiveTaxMoney: invoice.allExclusiveTaxMoney,
                    allInclusiveTaxMoney: invoice.allInclusiveTaxMoney,
                    allTaxMoney: invoice.allTaxMoney,
                    balanceDueMoney: this.$NP.minus(invoice.totalMoney, paidTotal, withholdingMoney),
                    paidMoney: paidTotal,
                    paidStatus: paidStatus,
                    subtotalMoney: invoice.subtotalMoney,
                    taxMoneyInfo: "",
                    totalMoney: invoice.totalMoney,
                    withholdingMoney: invoice.withholdingMoney,
                    totalWithholdingItemPrice: invoice.totalWithholdingItemPrice,
                    withholdingTaxName: invoice.withholdingTaxName,
                    withholdingRate: invoice.withholdingRate,
                    withholdingActive: invoice.withholdingActive,
                    versionCode: 1,
                    taxInfoUUIDArrayKey: this.getActiveUUIDArrayKey(taxInfoList),
                    emailRecord: "",
                    numPrefix: invoice.numPrefix,
                    markColor: 0,
                    isReceiptType: 0,
                    totalTaxInforString: invoice.totalTaxInforString,
                    jobDetails: invoice.jobDetails,
                    fromClient: 3,
                    invoiceSignatureUUIDArrayKey: ""
                };
            },
            saveInvoicePaymentFromDetail(invoiceDetail, payment) {
                let itemList = invoiceDetail.itemList == null ? [] : JSON.parse(JSON.stringify(invoiceDetail.itemList));
                let invoiceImageList = invoiceDetail.invoiceImageList == null ? [] : JSON.parse(JSON.stringify(invoiceDetail.invoiceImageList));
                let taxInfoList = invoiceDetail.taxInfoList == null ? [] : JSON.parse(JSON.stringify(invoiceDetail.taxInfoList));
                let payHistoryList = invoiceDetail.payHistoryList == null ? [] : JSON.parse(JSON.stringify(invoiceDetail.payHistoryList));
                payHistoryList.push(payment);

                for (let i = 0; i < taxInfoList.length; i++) {
                    taxInfoList[i].createDate = timest();
                    taxInfoList[i].accessDate = timest();
                    taxInfoList[i].updateTime = timest();
                }

                this.$axios.post('web_create_invoice', {
                    params: {
                        itemList: itemList.map((item, index) => this.buildInvoiceItemForSave(item, index)),
                        userClient: this.buildInvoiceClientForSave(invoiceDetail.userClient),
                        payHistoryList: payHistoryList,
                        invoiceImageList: invoiceImageList,
                        taxInfoList: taxInfoList,
                        invoice: this.buildInvoiceForPaymentSave(invoiceDetail.invoice, itemList, payHistoryList, invoiceImageList, taxInfoList, payment),
                        createOrUpdateInvoice: 2
                    }
                }).then(response => {
                    this.recordPaymentLoading = false;
                    if (response.data.errorCode === 0) {
                        this.recordPaymentDialog = false;
                        this.queryInvoiceDashboardSummary();
                        this.queryList();
                    }
                }).catch(() => {
                    this.recordPaymentLoading = false;
                })
            },
            //时间格式化
            dateFormat: function (row, column) {
                var timestamp = row[column.property] * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                return formatEnglishDate(timestamp);
            },
            //格式化过期时间
            formatDueDate: function (row, column) {
                let timestamp = row[column.property] * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                let dueDays = getDaysByDateString(Date.parse(new Date()), timestamp);
                if (dueDays === 0) {
                    return 'Today';
                } else if (dueDays < 0) {
                    return Math.abs(dueDays) + " days ago";
                } else {
                    return "in " + dueDays + " days";
                }
            },
            //格式化过期时间
            formatDueDate2: function (row, scope) {

                if (scope.row.totalMoney <= 0) {
                    return "";
                }
                let timestamp = row * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                let dueDays = getDaysByDateString(Date.parse(new Date()), timestamp);
                if (dueDays === 0) {
                    return 'Today';
                } else if (dueDays < 0) {
                    return Math.abs(dueDays) + " days ago";
                } else {
                    return "in " + dueDays + " days";
                }
            },

            //金钱格式化,增加金钱符号和分隔符
            amountFormat: function (row, column) {
                var columnAmount = row[column.property];
                if (columnAmount == undefined) {
                    return "";
                }
                return xiaoshudianfenge(columnAmount, this.userSetting);
            },
            formatSummaryMoney(value) {
                return xiaoshudianfenge(value, this.userSetting);
            }

        },
        created() {

        },
        data() {
            return {
                tableData: [],
                loading: true,
                currentPage: 1,
                totalCount: 0,
                pageSize: 50,
                searchContent: "",
                orderName: "invoiceStartDate",
                orderTpe: 2,
                userSetting: JSON.parse(localStorage.getItem('userSetting')),
                cantAddInvoice: false,
                totalTypeCount: 0,
                invoiceSummary: {
                    overdueInvoiceCount: 0,
                    overdueInvoiceTotalMoney: 0,
                    unpaidInvoiceCount: 0,
                    unpaidInvoiceTotalMoney: 0,
                    unsentInvoiceCount: 0,
                    unsentInvoiceTotalMoney: 0
                },
                recordPaymentDialog: false,
                recordPaymentLoading: false,
                recordPaymentInvoice: null,
                recordPaymentForm: {
                    paidMoney: 0,
                    paidDate: new Date(),
                    isFullPay: 0,
                    paidType: "",
                    note: ""
                }
            }
        },
        mounted() {
            this.queryCompanyAndInvoiceList();
            this.$watch('searchContent', debounce(() => {
                this.queryList();
            }, 500))
        }
    }
</script>
